import { SyntheticEvent } from 'react';

import { useOrderClients } from 'api/requests/order';
import { PrimaryClientType } from 'api/types/entity';
import { useSearchDebounce } from 'hooks/useSearchDebounce';
import { AutocompleteField } from 'libs/form/fields/AutocompleteField';
import { useTranslation } from 'libs/i18n';
import { AutocompleteValue } from 'libs/ui';
import { OrderFormType } from 'modules/order/common/forms/orderForm';

type ClientAutocompleteValue = AutocompleteValue<
  OrderFormType['client'],
  false
>;
export interface ClientAutocompleteFeatureProps {
  clientType: PrimaryClientType;
  disabled?: boolean;
  onChange?: (value: ClientAutocompleteValue) => void;
}

export const ClientAutocompleteFeature = ({
  clientType,
  disabled,
  onChange,
}: ClientAutocompleteFeatureProps) => {
  const { t } = useTranslation();

  const { search, debouncedFunc } = useSearchDebounce();

  const { listData, loading } = useOrderClients({
    pagination: { limit: 20, page: 0 },
    filter: {
      search,
    },
    params: { clientType },
  });

  const handleChange = (value: ClientAutocompleteValue) => {
    debouncedFunc('');
    onChange?.(value);
  };

  const onInputChange = (_: SyntheticEvent, v: string) => {
    debouncedFunc(v);
  };

  return (
    <AutocompleteField<Required<NonNullable<OrderFormType['client']>>, false>
      key={clientType ?? 'client-field'}
      disabledFilterOptions
      fullWidth
      showRequiredMark
      disabled={disabled}
      label={t('order.sections.clientSection.fields.client.label')}
      loading={loading}
      name="client"
      optionLabelKey="label"
      options={(listData?.list ?? []).map((record) => ({
        label: `${record.name} (${record.personalAccountNumber})`,
        value: record.id,
        record,
      }))}
      optionValueKey="value"
      placeholder={t('order.sections.clientSection.fields.client.placeholder')}
      onChange={handleChange}
      onInputChange={onInputChange}
    />
  );
};
